import React, { useEffect, useState } from 'react';

import { Theme, Icon, Text } from '@arc-ui/components';
import { BtIconArrowRight, BtIconCalendarAcceptedFill, BtIconTickFill } from '@arc-ui/icons';
import classNames from 'classnames';

import { useIsAuthenticated } from '@azure/msal-react';

import { ORDER_STATUS_COMPLETED } from 'features/order-tracker/services/constants';
import { Accordion, ExternalLink, HybridConnectInfo, StatusInfo } from 'features/order-tracker/shared-components';
import Dropdown from 'features/order-tracker/shared-components/DropDown/DropDown';
import { ArrowIcon, InfoIcon, SuccessCheckIcon } from 'features/order-tracker/shared-components/svg';
import {
  OPENREACH_ENGINEER_APPOINTMENT_TITLE,
  TRACKING_DELIVERY_DETAILS_FAILED,
} from 'features/order-tracker/store/sagas/services/helpers/constants';
import {
  MILESTONE_STATUSES,
  ORDER_SERVICES_TYPES,
  SERVICE_ACTIVATION_PRODUCT_STATUSES,
} from 'features/order-tracker/utils/entries';

import { BillingInfo, DeliveryInfo, LoginBenefitsBlock, MilestoneMessageBlock } from './components';
import {
  ATA_PHONE_ADAPTOR_SETUP_URL,
  ATA_PHONE_ADAPTOR_TITLE,
  CISCO_DESK_PHONE_SETUP_URL,
  CISCO_DESK_PHONE_TITLE,
  CLOSED_ACCORDION_LIST_TITLE,
  CVE_APP_PHONE_TITLE,
  CVE_APP_TITLE,
  CVE_EQUIPMENT_HEADING,
  CVE_HEADING,
  CVE_SETUP_URL,
  CVE_SUBTITLE,
  CVE_TITLE,
  FTTP_OPENREACH_APPT_LINK_ANALYTICS,
  FTTP_OPENREACH_APPT_LINK_URL,
  FTTP_PRODUCT_CATEGORY,
  LIVE_CHAT_TITLE,
  NON_FTTP_OPENREACH_APPT_LINK_ANALYTICS,
  NON_FTTP_OPENREACH_APPT_LINK_URL,
  OPENED_ACCORDION_LIST_TITLE,
  OPENREACH_APPT_LINK_LABEL,
  YEALINK_CORDLESS_PHONE_SETUP_URL,
  YEALINK_CORDLESS_PHONE_TITLE,
} from './constants';
import { getDeliveryAccordionAnalytics } from './helpers';

import styles from './styles.module.scss';

export const Milestone: React.FC<components.Milestone> = ({
  title,
  status,
  milestoneStatus,
  subTitle,
  formattedDate,
  formattedDateWithSlot,
  type,
  delivery,
  hasStatusLabel = false,
  isShowLoginBenefits,
  productCategory,
  displayTitle,
  id,
  productName,
  isAppAndPhoneCVE,
  smartHubDeliveryInfo: { isOrderDeliveriesInProgress, smartHubProduct, isHybridConnectAvailable },
}) => {
  const isAuthenticated = useIsAuthenticated();
  const [expandedAccordionInfo, setExpandedAccordionInfo] = useState(() => {
    if (type === ORDER_SERVICES_TYPES.Billing) {
      return status === ORDER_STATUS_COMPLETED;
    }

    return (isHybridConnectAvailable || !!smartHubProduct) && isOrderDeliveriesInProgress;
  });
  const [expandedServiceAccordion, setExpandedServiceAccordion] = useState(false);

  const [accordionAnalytics, setAccordionAnalytics] = useState('');
  const isShowLoginBenefitsList = !isAuthenticated && isShowLoginBenefits;
  const isDeliveryDetailsFailed = type === ORDER_SERVICES_TYPES.Delivery && status === TRACKING_DELIVERY_DETAILS_FAILED;
  const isExpandableMilestone = !!delivery || type === ORDER_SERVICES_TYPES.Billing;
  const isExpandableServiceMileStone =
    status === SERVICE_ACTIVATION_PRODUCT_STATUSES.BBAndCVEIncomplete ||
    status === SERVICE_ACTIVATION_PRODUCT_STATUSES.CVEProductIncomplete ||
    status === SERVICE_ACTIVATION_PRODUCT_STATUSES.BBAppointmentAndCVEIncomplete ||
    (status === SERVICE_ACTIVATION_PRODUCT_STATUSES.nonHaloIncomplete && productName?.includes('Cloud'));
  const isOpenReachAppointmentMilestone = title === OPENREACH_ENGINEER_APPOINTMENT_TITLE;
  const milestoneClassNames = classNames(styles.itemContent, { [styles.warningMilestone]: isDeliveryDetailsFailed });
  const hasHybridConnectInfo =
    isHybridConnectAvailable && isOpenReachAppointmentMilestone && !isOrderDeliveriesInProgress;
  const isFttpOpenReachAppt = productCategory === FTTP_PRODUCT_CATEGORY;
  const openReachLinkAnalytics = isFttpOpenReachAppt
    ? FTTP_OPENREACH_APPT_LINK_ANALYTICS
    : NON_FTTP_OPENREACH_APPT_LINK_ANALYTICS;
  const openReachLinkUrl = isFttpOpenReachAppt ? FTTP_OPENREACH_APPT_LINK_URL : NON_FTTP_OPENREACH_APPT_LINK_URL;

  const toggleAccordion = (): void => {
    isExpandableMilestone && setExpandedAccordionInfo(!expandedAccordionInfo);
  };
  const handleOnClick = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.sprChat('enable');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.sprChat('open');
  };

  const toggleServiceAccordion = (): void => {
    isExpandableServiceMileStone && setExpandedServiceAccordion(!expandedServiceAccordion);
  };

  useEffect(() => {
    if (type === ORDER_SERVICES_TYPES.Delivery) {
      const analyticsValue = getDeliveryAccordionAnalytics(title, expandedAccordionInfo, status);

      setAccordionAnalytics(analyticsValue);
    }
  }, [expandedAccordionInfo]);

  const openChat = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.sprChat('enable');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.sprChat('open');
  };

  const handleCVEClick = (url) => {
    window.open(url);
  };
  const element = (
    <div
      className={classNames(styles.milestoneContainer, {
        [styles.isOpenreachEngineerAppointment]: isOpenReachAppointmentMilestone,
      })}
      style={{ width: '100%' }}
    >
      <div
        className={classNames(styles.statusIcon, {
          [styles.complete]: milestoneStatus === MILESTONE_STATUSES.complete,
          [styles.incomplete]: milestoneStatus === MILESTONE_STATUSES.incomplete,
          [styles.inProgress]: milestoneStatus === MILESTONE_STATUSES.inProgress,
        })}
      >
        <SuccessCheckIcon />
      </div>
      {hasStatusLabel && <StatusInfo className={styles.label} status={milestoneStatus} />}
      <InfoIcon className={styles.infoIcon} width={28} height={28} />
      <div style={{ display: 'flex' }}>
        <h4>{displayTitle || title}</h4>
        {id?.includes('Incomplete') && (
          <div style={{ marginLeft: 'auto' }}>
            <Theme >
              <Text size="s" isInline>
                <Icon isInline size={24} icon={BtIconCalendarAcceptedFill} /> Scheduled
              </Text>
            </Theme >
          </div>
        )}{' '}
        {id?.includes('Completed') && (
          <div style={{ marginLeft: 'auto' }}>
            <Theme >
              <Text size="s" isInline>
                <Icon color="statusGreen" isInline size={24} icon={BtIconTickFill} /> Ready to use
              </Text>
            </Theme >
          </div>
        )}{' '}
      </div>
      {subTitle && <p className={styles.subTitle} style={productName?.includes('Cloud')?{ marginBottom:'12px' }:{}}>{subTitle}</p>}
      {!formattedDateWithSlot ? (
        <MilestoneMessageBlock status={status} formattedDate={formattedDate} title={title} />
      ) : (
        <p className={styles.dateTimeSlotInfo}>{formattedDateWithSlot}</p>
      )}
      {displayTitle === 'Expert Setup' && (
        <p className={styles.subTitle}>
          Anything need changing? Contact us on{' '}
          {/* <a className={styles.link} onClick={handleOnClick} rel="noopener noreferrer" role='Open Live Chat '>
            {' '}
          
          </a> */}
          <span
            role="button"
            tabIndex={0}
            onClick={handleOnClick}
            onKeyDown={handleOnClick}
            title="Chat now"
            className={styles.link}
          >
            Live Chat
          </span>
        </p>
      )}
      {isOpenReachAppointmentMilestone && (
        <ExternalLink
          text={OPENREACH_APPT_LINK_LABEL}
          link={openReachLinkUrl}
          analytics={openReachLinkAnalytics}
          className={styles.externalLink}
          iconClassName={styles.arrowIcon}
        />
      )}
      {hasHybridConnectInfo && (
        <HybridConnectInfo isDeliveryInProgress={isOrderDeliveriesInProgress} smartHubProduct={smartHubProduct} />
      )}
    </div>
  );

  if (isExpandableMilestone) {
    return (
      <div className={styles.itemContainer}>
        <Accordion
          title={element}
          analytics={accordionAnalytics}
          toggleEvent={toggleAccordion}
          isExpanded={expandedAccordionInfo}
        >
          {delivery ? (
            <DeliveryInfo delivery={delivery} status={status as common.DeliveryStatus} />
          ) : (
            <BillingInfo isAuthenticated={isAuthenticated} />
          )}
        </Accordion>
        {isShowLoginBenefitsList && (
          <LoginBenefitsBlock
            title={expandedAccordionInfo ? OPENED_ACCORDION_LIST_TITLE : CLOSED_ACCORDION_LIST_TITLE}
          />
        )}
      </div>
    );
  }
  if (isExpandableServiceMileStone && productName?.includes('Cloud')) {
    return (
      <div className={styles.itemContainer}>
        <Accordion
          title={element}
          analytics={accordionAnalytics}
          toggleEvent={toggleServiceAccordion}
          isExpanded={expandedServiceAccordion}
        >
          <div>{isAppAndPhoneCVE ? CVE_APP_PHONE_TITLE : CVE_APP_TITLE}</div>
          {!isAppAndPhoneCVE && (
            <a target="_blank" tabIndex={0} href={CVE_SETUP_URL} className={styles.link} rel="noreferrer">
              {' '}
              Download Cloud Voice Express app <ArrowIcon height={8} width={8} />
            </a>
          )}
          {isAppAndPhoneCVE && (
            <Dropdown>
              <h4>{CVE_HEADING}</h4>
              <div className={styles.spacing} />
              <p>{CVE_SUBTITLE}</p>
              <div
                role="button"
                tabIndex={0}
                className={styles.card}
                onClick={() => handleCVEClick(CVE_SETUP_URL)}
                onKeyDown={() => handleCVEClick(CVE_SETUP_URL)}
              >
                <div className={styles.cloudIcon} />
                <h5>{CVE_TITLE}</h5>
                <div style={{ marginLeft: 'auto' }}>
                  <Theme >
                    <Icon color="auto" isInline size={24} icon={BtIconArrowRight} />
                  </Theme >
                </div>
              </div>

              <p>{CVE_EQUIPMENT_HEADING}</p>
              <div
                role="button"
                className={styles.card}
                tabIndex={0}
                onClick={() => handleCVEClick(YEALINK_CORDLESS_PHONE_SETUP_URL)}
                onKeyDown={() => handleCVEClick(YEALINK_CORDLESS_PHONE_SETUP_URL)}
              >
                <div className={styles.cordLessPhoneIcon} />
                <h5>{YEALINK_CORDLESS_PHONE_TITLE}</h5>
                <div style={{ marginLeft: 'auto' }}>
                  <Theme >
                    <Icon color="auto" isInline size={24} icon={BtIconArrowRight} />
                  </Theme >
                </div>
              </div>

              <div
                role="button"
                tabIndex={0}
                className={styles.card}
                onClick={() => handleCVEClick(CISCO_DESK_PHONE_SETUP_URL)}
                onKeyDown={() => handleCVEClick(CISCO_DESK_PHONE_SETUP_URL)}
              >
                <div className={styles.deskPhoneIcon} />
                <h5>{CISCO_DESK_PHONE_TITLE}</h5>
                <div style={{ marginLeft: 'auto' }}>
                  <Theme >
                    <Icon color="auto" isInline size={24} icon={BtIconArrowRight} />
                  </Theme >
                </div>
              </div>

              <div
                role="button"
                className={styles.card}
                tabIndex={0}
                onClick={() => handleCVEClick(ATA_PHONE_ADAPTOR_SETUP_URL)}
                onKeyDown={() => handleCVEClick(ATA_PHONE_ADAPTOR_SETUP_URL)}
              >
                <div className={styles.adaptorIcon} />
                <h5>{ATA_PHONE_ADAPTOR_TITLE}</h5>
                <div style={{ marginLeft: 'auto' }}>
                  <Theme >
                    <Icon color="auto" isInline size={24} icon={BtIconArrowRight} />
                  </Theme >
                </div>
              </div>
            </Dropdown>
          )}
          <div className={styles.spacing} />
          <p>{LIVE_CHAT_TITLE}</p>
          <div
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={openChat}
            onKeyDown={openChat}
            className={styles.link}
          >
            {' '}
            Live chat <ArrowIcon height={8} width={8} />
          </div>
        </Accordion>
      </div>
    );
  }

  return (
    <div className={styles.itemContainer}>
      <div data-testid="milestone" className={milestoneClassNames}>
        {element}
      </div>
    </div>
  );
};
